import { request } from './client.js';

/**
 * Библиотеки героев, вещей и локаций (E7). Формы ответов дублируют
 * `apps/api/src/modules/{characters,things,locations}/schema.ts`: общий пакет
 * контрактов заводить рано (02-ARCHITECTURE.md §3).
 */

export type RenditionStatus = 'empty' | 'generating' | 'needs_review' | 'approved' | 'failed';
export type GenerationWarning = 'photos_missing' | 'photos_incomplete' | 'photo_low_resolution';
export type CharacterKind = 'child' | 'adult' | 'pet';
export type CharacterPhotoSlot = 'front' | 'profile' | 'full' | 'extra';
export type RequiredPhotoSlot = 'front' | 'profile' | 'full';
export type PhotoIssueCode = 'slot_missing' | 'low_resolution' | 'duplicate_of_other_slot';
export type ThingCategory = 'toy' | 'clothing' | 'dish' | 'furniture' | 'other';
export type LocationKind = 'room' | 'kitchen' | 'outdoor' | 'transport' | 'imaginary' | 'other';

export const CHARACTER_KINDS: readonly CharacterKind[] = ['child', 'adult', 'pet'];
export const REQUIRED_PHOTO_SLOTS: readonly RequiredPhotoSlot[] = ['front', 'profile', 'full'];
export const THING_CATEGORIES: readonly ThingCategory[] = [
  'toy',
  'clothing',
  'dish',
  'furniture',
  'other',
];
export const LOCATION_KINDS: readonly LocationKind[] = [
  'room',
  'kitchen',
  'outdoor',
  'transport',
  'imaginary',
  'other',
];

/**
 * После третьей попытки крутить генерацию бессмысленно: виноваты не модель
 * и не промпт, а исходные фото (01-SPEC.md §5.5). Вместо кнопки «ещё раз»
 * показывается разбор.
 */
export const DEADLOCK_ATTEMPTS = 3;

export interface VariantDto {
  readonly id: string;
  readonly assetId: string;
  readonly url: string;
  readonly urlExpiresAt: string;
  readonly seed: string | null;
  readonly modelId: string;
  readonly promptSnapshot: string;
  readonly createdAt: string;
}

export interface RenditionDto {
  readonly id: string;
  readonly styleId: string;
  readonly status: RenditionStatus;
  readonly selectedVariantId: string | null;
  readonly approvedAt: string | null;
  readonly attempts: number;
  readonly lastError: string | null;
  readonly lastRefAssetIds: readonly string[];
  readonly variants: readonly VariantDto[];
}

export interface PhotoDto {
  readonly assetId: string;
  readonly url: string;
  readonly urlExpiresAt: string;
  readonly width: number | null;
  readonly height: number | null;
  readonly order: number;
}

export interface CharacterPhotoDto extends PhotoDto {
  readonly slot: CharacterPhotoSlot;
}

export interface UsageDto {
  readonly books: number;
  readonly activeBooks: number;
}

export interface AppearanceDto {
  readonly hair: string;
  readonly eyes: string;
  readonly marks: string;
  readonly outfit: string;
  readonly build: string;
}

export interface PhotoReviewDto {
  readonly filledSlots: readonly CharacterPhotoSlot[];
  readonly missingSlots: readonly RequiredPhotoSlot[];
  readonly issues: readonly {
    readonly slot: CharacterPhotoSlot;
    readonly assetId: string | null;
    readonly code: PhotoIssueCode;
  }[];
}

export interface CharacterDto {
  readonly id: string;
  readonly name: string;
  readonly displayName: string | null;
  readonly kind: CharacterKind;
  readonly role: string | null;
  readonly ageYears: number | null;
  readonly gender: string | null;
  readonly traits: readonly string[];
  readonly appearance: AppearanceDto;
  readonly favouriteActivities: readonly string[];
  readonly catchphrase: string | null;
  readonly promptEn: string | null;
  readonly status: 'draft' | 'ready';
  readonly archivedAt: string | null;
  readonly photos: readonly CharacterPhotoDto[];
  readonly photoReview: PhotoReviewDto;
  readonly renditions: readonly RenditionDto[];
  readonly usage: UsageDto;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ThingDto {
  readonly id: string;
  readonly name: string;
  readonly category: ThingCategory;
  readonly descriptionRu: string | null;
  readonly promptEn: string | null;
  readonly status: 'draft' | 'ready';
  readonly archivedAt: string | null;
  readonly photos: readonly PhotoDto[];
  readonly renditions: readonly RenditionDto[];
  readonly usage: UsageDto;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface LocationDto {
  readonly id: string;
  readonly name: string;
  readonly kind: LocationKind;
  readonly descriptionRu: string | null;
  readonly promptEn: string | null;
  readonly status: 'draft' | 'ready';
  readonly archivedAt: string | null;
  readonly photos: readonly PhotoDto[];
  readonly renditions: readonly RenditionDto[];
  readonly usage: UsageDto;
  readonly spreads: readonly { sceneId: string; bookId: string; index: number }[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface GenerationAcceptedDto {
  readonly generationId: string;
  readonly renditionId: string;
  readonly status: RenditionStatus;
  readonly warnings: readonly GenerationWarning[];
}

export interface UpdateCharacterInput {
  readonly name?: string;
  readonly displayName?: string | null;
  readonly kind?: CharacterKind;
  readonly role?: string | null;
  readonly ageYears?: number | null;
  readonly gender?: string | null;
  readonly traits?: readonly string[];
  readonly favouriteActivities?: readonly string[];
  readonly catchphrase?: string | null;
}

const query = (styleId?: string): string => (styleId ? `?styleId=${styleId}` : '');

/** Состав книги: герой плюс его роль в этой книге. `isLead` — только здесь (§3.8). */
export interface BookCastDto extends CharacterDto {
  readonly isLead: boolean;
}

export const charactersApi = {
  list: (styleId?: string) => request<{ items: CharacterDto[] }>(`/characters${query(styleId)}`),
  get: (id: string) => request<CharacterDto>(`/characters/${id}`),
  create: (body: { name: string; kind: CharacterKind }) =>
    request<CharacterDto>('/characters', { method: 'POST', body }),
  update: (id: string, body: UpdateCharacterInput) =>
    request<CharacterDto>(`/characters/${id}`, { method: 'PATCH', body }),
  setPhotos: (
    id: string,
    photos: readonly { assetId: string; slot: CharacterPhotoSlot; order: number }[],
  ) => request<CharacterDto>(`/characters/${id}/photos`, { method: 'POST', body: { photos } }),
  ensureRendition: (id: string, styleId: string) =>
    request<CharacterDto>(`/characters/${id}/renditions`, { method: 'POST', body: { styleId } }),
  archive: (id: string) => request<{ id: string }>(`/characters/${id}/archive`, { method: 'POST' }),
  remove: (id: string) => request<{ id: string }>(`/characters/${id}`, { method: 'DELETE' }),
  supportRequest: (id: string, body: { renditionId?: string; comment?: string }) =>
    request<{ status: 'ok' }>(`/characters/${id}/support-request`, { method: 'POST', body }),
  supportGet: (id: string) => request<CharacterDto>(`/support/characters/${id}`),
  generate: (renditionId: string, bookId?: string) =>
    request<GenerationAcceptedDto>(`/character-renditions/${renditionId}/generate`, {
      method: 'POST',
      body: bookId ? { bookId } : {},
    }),
  edit: (renditionId: string, variantId: string, guidance: string, bookId?: string) =>
    request<GenerationAcceptedDto>(`/character-renditions/${renditionId}/edit`, {
      method: 'POST',
      body: { variantId, guidance, ...(bookId ? { bookId } : {}) },
    }),
  selectVariant: (renditionId: string, variantId: string) =>
    request<CharacterDto>(`/character-renditions/${renditionId}/select-variant`, {
      method: 'POST',
      body: { variantId },
    }),
  approve: (renditionId: string) =>
    request<CharacterDto>(`/character-renditions/${renditionId}/approve`, { method: 'POST' }),
  forBook: (bookId: string) => request<{ items: BookCastDto[] }>(`/books/${bookId}/characters`),
  linkToBook: (
    bookId: string,
    items: readonly { characterId: string; order: number; isLead: boolean }[],
  ) =>
    request<{ items: BookCastDto[] }>(`/books/${bookId}/characters`, {
      method: 'POST',
      body: { items },
    }),
};

export const thingsApi = {
  list: (styleId?: string) => request<{ items: ThingDto[] }>(`/things${query(styleId)}`),
  get: (id: string) => request<ThingDto>(`/things/${id}`),
  create: (body: { name: string; category: ThingCategory; descriptionRu?: string | null }) =>
    request<ThingDto>('/things', { method: 'POST', body }),
  update: (
    id: string,
    body: {
      name?: string;
      category?: ThingCategory;
      descriptionRu?: string | null;
      promptEn?: string | null;
    },
  ) => request<ThingDto>(`/things/${id}`, { method: 'PATCH', body }),
  composePrompt: (id: string) => request<ThingDto>(`/things/${id}/prompt`, { method: 'POST' }),
  setPhotos: (id: string, photos: readonly { assetId: string; order: number }[]) =>
    request<ThingDto>(`/things/${id}/photos`, { method: 'POST', body: { photos } }),
  ensureRendition: (id: string, styleId: string) =>
    request<ThingDto>(`/things/${id}/renditions`, { method: 'POST', body: { styleId } }),
  archive: (id: string) => request<{ id: string }>(`/things/${id}/archive`, { method: 'POST' }),
  remove: (id: string) => request<{ id: string }>(`/things/${id}`, { method: 'DELETE' }),
  generate: (renditionId: string, bookId?: string) =>
    request<GenerationAcceptedDto>(`/thing-renditions/${renditionId}/generate`, {
      method: 'POST',
      body: bookId ? { bookId } : {},
    }),
  edit: (renditionId: string, variantId: string, guidance: string, bookId?: string) =>
    request<GenerationAcceptedDto>(`/thing-renditions/${renditionId}/edit`, {
      method: 'POST',
      body: { variantId, guidance, ...(bookId ? { bookId } : {}) },
    }),
  selectVariant: (renditionId: string, variantId: string) =>
    request<ThingDto>(`/thing-renditions/${renditionId}/select-variant`, {
      method: 'POST',
      body: { variantId },
    }),
  forBook: (bookId: string) => request<{ items: ThingDto[] }>(`/books/${bookId}/things`),
  linkToBook: (bookId: string, items: readonly { thingId: string; order: number }[]) =>
    request<{ items: ThingDto[] }>(`/books/${bookId}/things`, { method: 'POST', body: { items } }),
};

export const locationsApi = {
  list: (styleId?: string) => request<{ items: LocationDto[] }>(`/locations${query(styleId)}`),
  get: (id: string) => request<LocationDto>(`/locations/${id}`),
  create: (body: { name: string; kind: LocationKind; descriptionRu?: string | null }) =>
    request<LocationDto>('/locations', { method: 'POST', body }),
  update: (
    id: string,
    body: {
      name?: string;
      kind?: LocationKind;
      descriptionRu?: string | null;
      promptEn?: string | null;
    },
  ) => request<LocationDto>(`/locations/${id}`, { method: 'PATCH', body }),
  composePrompt: (id: string) =>
    request<LocationDto>(`/locations/${id}/prompt`, { method: 'POST' }),
  setPhotos: (id: string, photos: readonly { assetId: string; order: number }[]) =>
    request<LocationDto>(`/locations/${id}/photos`, { method: 'POST', body: { photos } }),
  ensureRendition: (id: string, styleId: string) =>
    request<LocationDto>(`/locations/${id}/renditions`, { method: 'POST', body: { styleId } }),
  archive: (id: string) => request<{ id: string }>(`/locations/${id}/archive`, { method: 'POST' }),
  remove: (id: string) => request<{ id: string }>(`/locations/${id}`, { method: 'DELETE' }),
  generate: (renditionId: string, bookId?: string) =>
    request<GenerationAcceptedDto>(`/location-renditions/${renditionId}/generate`, {
      method: 'POST',
      body: bookId ? { bookId } : {},
    }),
  edit: (renditionId: string, variantId: string, guidance: string, bookId?: string) =>
    request<GenerationAcceptedDto>(`/location-renditions/${renditionId}/edit`, {
      method: 'POST',
      body: { variantId, guidance, ...(bookId ? { bookId } : {}) },
    }),
  selectVariant: (renditionId: string, variantId: string) =>
    request<LocationDto>(`/location-renditions/${renditionId}/select-variant`, {
      method: 'POST',
      body: { variantId },
    }),
  forBook: (bookId: string) => request<{ items: LocationDto[] }>(`/books/${bookId}/locations`),
  linkToBook: (bookId: string, items: readonly { locationId: string; order: number }[]) =>
    request<{ items: LocationDto[] }>(`/books/${bookId}/locations`, {
      method: 'POST',
      body: { items },
    }),
};

export const photoConsentApi = {
  /**
   * Granting carries the edition whose text was on the screen; withdrawal carries
   * nothing, because there is no text to record having been shown (§2.6). The two
   * bodies differ in shape for the same reason the server's contract does.
   */
  set: (body: { accepted: true; version: string } | { accepted: false }) =>
    request<{ consentPhotoProcessing: boolean }>('/me/photo-consent', {
      method: 'POST',
      body,
    }),
};

/**
 * Разбор «не похож» показывается начиная с четвёртой попытки (§5.5).
 * Новая попытка снова осмысленна, если фотографии с тех пор поменяли —
 * поэтому кнопка возвращается не по счётчику, а по составу референсов.
 */
export function isDeadlocked(
  rendition: RenditionDto | undefined,
  photos: readonly { readonly assetId: string }[],
): boolean {
  if (!rendition) return false;
  if (rendition.status === 'approved' || rendition.status === 'generating') return false;
  if (rendition.attempts < DEADLOCK_ATTEMPTS) return false;
  return !photosChangedSince(rendition, photos);
}

/** Поменялся ли состав фотографий с последней попытки. */
export function photosChangedSince(
  rendition: RenditionDto,
  photos: readonly { readonly assetId: string }[],
): boolean {
  const current = photos.map((photo) => photo.assetId);
  const previous = rendition.lastRefAssetIds;
  return (
    current.length !== previous.length ||
    current.some((assetId, index) => assetId !== previous[index])
  );
}

/** Рендер книги — тот, что нарисован в её стиле (§3.4). */
export function renditionFor(
  entity: { readonly renditions: readonly RenditionDto[] },
  styleId: string,
): RenditionDto | undefined {
  return entity.renditions.find((rendition) => rendition.styleId === styleId);
}

/**
 * What to show for a library item: its chosen drawing — in this style when one is asked
 * for, else the most recently approved in any — and only then its first photo. Photos are
 * deleted when a drawing is approved, so a photo-only preview left every approved
 * character a row of text.
 */
export function previewImage(
  entity: {
    readonly renditions: readonly RenditionDto[];
    readonly photos: readonly { readonly url: string }[];
  },
  styleId?: string | null,
): string | undefined {
  const chosen = (rendition: RenditionDto | undefined) =>
    rendition?.variants.find((variant) => variant.id === rendition.selectedVariantId)?.url;
  const inStyle = styleId ? chosen(renditionFor(entity, styleId)) : undefined;
  const latest = [...entity.renditions]
    .filter((rendition) => rendition.selectedVariantId)
    .sort((a, b) => (b.approvedAt ?? '').localeCompare(a.approvedAt ?? ''))
    .map(chosen)
    .find(Boolean);
  return inStyle ?? latest ?? entity.photos[0]?.url;
}

/** Герой готов к иллюстрациям, только когда его лист утверждён в стиле книги. */
export function isApproved(character: CharacterDto, styleId: string): boolean {
  return renditionFor(character, styleId)?.status === 'approved';
}

/** У вещи или локации выбран готовый референс; отдельного утверждения у них нет. */
export function hasReadySelection(
  entity: { readonly renditions: readonly RenditionDto[] },
  styleId: string,
): boolean {
  const rendition = renditionFor(entity, styleId);
  return Boolean(
    rendition?.selectedVariantId &&
    (rendition.status === 'needs_review' || rendition.status === 'approved'),
  );
}
